import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import AnalyzerForm from "./components/AnalyzerForm";
import Results from "./components/Results";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''; // Empty base URL will use the proxy in package.json for development

function App() {
  const [results, setResults] = useState(null);
  const [criticalError, setCriticalError] = useState(null);
  const [analysisState, setAnalysisState] = useState("idle"); // 'idle', 'analyzing', 'results_ready', 'error_critical'
  const [progressUpdates, setProgressUpdates] = useState([]); // To store task updates
  const eventSourceRef = useRef(null); // To hold the EventSource instance
  const progressUpdatesRef = useRef([]); // Ref to track progress updates

  // Update the ref when progressUpdates changes
  useEffect(() => {
    progressUpdatesRef.current = progressUpdates;
  }, [progressUpdates]);

  // Clean up event source on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const analyzeWebsite = useCallback((
    url,
    testMode = false,
    useSemrushCheckbox = true,
    usePagespeedCheckbox = true
  ) => {
    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Reset state
    setResults(null);
    setCriticalError(null);
    setAnalysisState("analyzing");
    setProgressUpdates([]);
    progressUpdatesRef.current = [];

    // Set up parameters for the API request
    const params = new URLSearchParams({
      url,
      test_mode: testMode,
      use_semrush_checkbox: useSemrushCheckbox,
      use_pagespeed_checkbox: usePagespeedCheckbox,
    });

    console.log('Creating EventSource connection to:', `${API_BASE_URL}/api/analyze?${params.toString()}`);
    
    // Create new EventSource connection
    const evtSource = new EventSource(`${API_BASE_URL}/api/analyze?${params.toString()}`, {
      withCredentials: false // Ensure no credentials are sent to avoid CORS preflight
    });
    
    // Store the EventSource in the ref
    eventSourceRef.current = evtSource;
    
    // Debug listener for connection opening
    evtSource.onopen = () => {
      console.log('SSE Connection opened successfully');
      setCriticalError(null); // Clear any previous errors
    };

    // Handle connection errors
    const handleError = (error) => {
      console.error('EventSource error:', error);
      
      // Only update state if we're still in the analyzing state
      setAnalysisState(prevState => {
        if (prevState === 'analyzing') {
          setCriticalError('Failed to connect to the analysis server. Please try again.');
          return 'error_critical';
        }
        return prevState;
      });
      
      // Close the connection
      if (evtSource) {
        evtSource.close();
        if (eventSourceRef.current === evtSource) {
          eventSourceRef.current = null;
        }
      }
    };

    // Handle incoming messages
    const handleMessage = (event) => {
      try {
        if (!event.data) {
          console.log("Received empty data in SSE message");
          return;
        }
        
        console.log("Raw SSE message received:", event.data);
        const data = JSON.parse(event.data);
        console.log("SSE Progress Update (parsed):", data);
        
        // Handle TASK_UPDATE events
        if (data.type === "TASK_UPDATE") {
          console.log("Processing task update:", data.taskId, data.status);
          
          // Force state update even when the same taskId is updated
          setProgressUpdates(prev => {
            console.log("Current progress updates:", prev);
            const existingIndex = prev.findIndex(update => update.taskId === data.taskId);
            if (existingIndex >= 0) {
              // Update existing task with timestamp to force re-render
              console.log("Updating existing task:", data.taskId);
              const newUpdates = [...prev];
              newUpdates[existingIndex] = {
                ...data,
                _timestamp: Date.now() // Add timestamp to ensure React detects the change
              };
              return newUpdates;
            } else {
              // Add new task
              console.log("Adding new task:", data.taskId);
              return [...prev, {
                ...data,
                _timestamp: Date.now()
              }];
            }
          });
        }
      } catch (e) {
        console.error("Failed to parse progress update:", event.data, e);
      }
    };

    // Handle analysis complete event
    const handleAnalysisComplete = (event) => {
      try {
        const eventData = JSON.parse(event.data);
        console.log("SSE Analysis Complete:", eventData);
        
        if (eventData.type === "ANALYSIS_COMPLETE" && eventData.results) {
          setResults(eventData.results);
          setAnalysisState("results_ready");
        } else {
          console.error("Received analysis_complete event with unexpected data:", eventData);
          setCriticalError("Analysis completed but data format was unexpected.");
          setAnalysisState("error_critical");
        }
      } catch (e) {
        console.error("Failed to parse analysis_complete event:", event.data, e);
        setCriticalError("Failed to parse analysis completion data.");
        setAnalysisState("error_critical");
      } finally {
        // Close the connection
        if (evtSource) {
          evtSource.close();
          if (eventSourceRef.current === evtSource) {
            eventSourceRef.current = null;
          }
        }
      }
    };

    // Handle analysis error event
    const handleAnalysisError = (event) => {
      try {
        const errorData = JSON.parse(event.data);
        console.error("SSE Analysis Error Event:", errorData);
        setCriticalError(
          errorData.message || "An unknown critical error occurred during analysis."
        );
        setAnalysisState("error_critical");
      } catch (e) {
        console.error("Failed to parse analysis_error event:", event.data, e);
        setCriticalError("An unparseable critical error occurred.");
        setAnalysisState("error_critical");
      }
      
      if (evtSource) {
        evtSource.close();
        if (eventSourceRef.current === evtSource) {
          eventSourceRef.current = null;
        }
      }
    };

    // Set up event listeners
    evtSource.onerror = handleError;
    evtSource.onmessage = handleMessage;
    evtSource.addEventListener("analysis_complete", handleAnalysisComplete);
    evtSource.addEventListener("analysis_error", handleAnalysisError);

    return () => {
      // Cleanup function to close the EventSource when the component unmounts
      if (evtSource) {
        evtSource.onerror = null;
        evtSource.onmessage = null;
        evtSource.removeEventListener("analysis_complete", handleAnalysisComplete);
        evtSource.removeEventListener("analysis_error", handleAnalysisError);
        evtSource.close();
      }
      if (eventSourceRef.current === evtSource) {
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Cleanup EventSource on component unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        console.log("EventSource closed on component unmount.");
      }
    };
  }, []);

  // Function to get status emoji based on task status
  const getStatusEmoji = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'running':
        return '🔄';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  // Function to get status class for styling
  const getStatusClass = (status) => {
    return status.toLowerCase().replace('_', '-');
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Website Analyzer</h1>
        <p>Analyze any website for WordPress, SSL, and more!</p>
      </header>
      <main>
        <AnalyzerForm 
          onSubmit={analyzeWebsite} 
          isAnalyzing={analysisState === "analyzing"} 
        />
        
        {/* Progress Updates */}
        {analysisState === "analyzing" && (
          <div className="progress-container">
            <h3>Analysis in Progress</h3>
            <div className="progress-steps">
              {progressUpdates.length === 0 ? (
                <div className="progress-message">Initializing analysis...</div>
              ) : (
                <div className="task-list">
                  {progressUpdates.map((task, index) => (
                    <div 
                      key={`${task.taskId}-${index}`} 
                      className={`task-item ${getStatusClass(task.status)}`}
                    >
                      <span className="task-status">{getStatusEmoji(task.status)}</span>
                      <span className="task-name">{task.taskName}</span>
                      <span className="task-status-text">
                        {task.status === 'running' ? 'In progress...' : 
                         task.status === 'completed' ? 'Completed' : 
                         task.status === 'error' ? 'Failed' : task.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {criticalError && (
          <div className="error-message">
            <h3>Error</h3>
            <p>{criticalError}</p>
          </div>
        )}

        {/* Results */}
        {analysisState === "results_ready" && results && (
          <Results data={results} />
        )}
      </main>
      <footer>
        <p>
          &copy; {new Date().getFullYear()} Website Analyzer - Built with React
          & Flask
        </p>
      </footer>
    </div>
  );
}

export default App;
